import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { getMaterials } from "@/api";
import { EditMaterialModal } from "@/modals/Materials/EditMaterialModal";
import type { Material } from "@/types";
import { Table } from "@/ui";
import { getMaterialsTableColumns } from "./MaterialsTableColumns";

interface MaterialsTableProps {
  searchQuery: string;
}

export const MaterialsTable = ({ searchQuery }: MaterialsTableProps) => {
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(
    null,
  );

  const { data: materialsData, isLoading } = useQuery({
    queryKey: ["materials", "getMaterials"],
    queryFn: () => getMaterials(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // Filter materials by search query
  const filteredMaterials = useMemo(() => {
    return (materialsData?.data ?? []).filter((material) =>
      material.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [materialsData?.data, searchQuery]);

  const columns = useMemo(() => getMaterialsTableColumns(), []);

  const table = useReactTable({
    data: filteredMaterials,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleRowClick = (row: Material) => {
    setSelectedMaterial(row);
  };

  return (
    <>
      <Table
        table={table}
        handleRowClick={handleRowClick}
        isLoading={isLoading}
      />
      <div className="flex items-center justify-between rounded-b-2xl border border-neutral-200 px-4 py-3">
        <span className="text-sm text-neutral-500">
          {table.getRowModel().rows.length}{" "}
          {table.getRowModel().rows.length === 1 ? "material" : "materials"}
        </span>
      </div>
      {selectedMaterial && (
        <EditMaterialModal
          material={selectedMaterial}
          show={!!selectedMaterial}
          onClose={() => setSelectedMaterial(null)}
        />
      )}
    </>
  );
};
